/**
 * PurchasesList
 *
 * Displays substrate log purchases with the ability to record new ones.
 */

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Plus, Trash2, Loader2, Package } from 'lucide-react'
import RecordPurchaseModal from './RecordPurchaseModal'

// Shiitake strain ID will be looked up from the first purchase or passed as prop
// For now we need to fetch it from the strains table
import { supabase } from '@/supabase-client'
import { useEffect } from 'react'

export default function PurchasesList({ hook }) {
  const { purchases, totalLogsInStorage, addPurchase, deletePurchase, isLoading } = hook
  const [showModal, setShowModal] = useState(false)
  const [shiitakeStrainId, setShiitakeStrainId] = useState(null)
  const [deleting, setDeleting] = useState(null)

  // Fetch shiitake strain ID once
  useEffect(() => {
    async function fetchStrainId() {
      const { data } = await supabase
        .from('strains')
        .select('id')
        .eq('code', 'SHI')
        .single()
      if (data) setShiitakeStrainId(data.id)
    }
    fetchStrainId()
  }, [])

  const handleDelete = async (id) => {
    if (!confirm('Delete this purchase record? This cannot be undone.')) return
    setDeleting(id)
    try {
      await deletePurchase(id)
    } catch (err) {
      console.error('Failed to delete purchase:', err)
    } finally {
      setDeleting(null)
    }
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total Purchased</div>
            <div className="text-2xl font-bold">
              {purchases.reduce((sum, p) => sum + p.quantity, 0)} logs
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">In Cold Storage</div>
            <div className="text-2xl font-bold">{totalLogsInStorage} logs</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Logs Used</div>
            <div className="text-2xl font-bold">
              {purchases.reduce((sum, p) => sum + (p.quantity - (p.logs_remaining || 0)), 0)} logs
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total Spent</div>
            <div className="text-2xl font-bold">
              £{purchases.reduce((sum, p) => sum + Number(p.total_cost || 0), 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Purchases Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Log Purchases
          </CardTitle>
          <Button onClick={() => setShowModal(true)} size="sm" disabled={!shiitakeStrainId}>
            <Plus className="mr-2 h-4 w-4" />
            Record Purchase
          </Button>
        </CardHeader>
        <CardContent>
          {purchases.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No purchases recorded yet. Click "Record Purchase" to add your first log purchase.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Weight/Log</TableHead>
                  <TableHead className="text-right">Cost/Log</TableHead>
                  <TableHead className="text-right">Total Cost</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchases.map((purchase) => (
                  <TableRow key={purchase.id}>
                    <TableCell className="font-mono text-muted-foreground">{purchase.purchase_code}</TableCell>
                    <TableCell>{formatDate(purchase.purchase_date)}</TableCell>
                    <TableCell>{purchase.supplier_name}</TableCell>
                    <TableCell className="text-right">{purchase.quantity}</TableCell>
                    <TableCell className="text-right">{purchase.unit_weight_kg} kg</TableCell>
                    <TableCell className="text-right">£{Number(purchase.cost_per_log).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-medium">£{Number(purchase.total_cost).toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={purchase.logs_remaining > 0 ? 'default' : 'secondary'}>
                        {purchase.logs_remaining}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {purchase.logs_remaining === purchase.quantity && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(purchase.id)}
                          disabled={deleting === purchase.id}
                        >
                          {deleting === purchase.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-destructive" />
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {shiitakeStrainId && (
        <RecordPurchaseModal
          open={showModal}
          onOpenChange={setShowModal}
          onSubmit={addPurchase}
          strainId={shiitakeStrainId}
        />
      )}
    </div>
  )
}
